/**
 * Source of truth for the project-file classifier structured output.
 *
 * The Zod schema {@link projectFileClassifierOutputSchema} defines the wire
 * contract; the JSON Schema handed to OpenAI is derived via
 * {@link z.toJSONSchema} so there is no drift between the contract we ask
 * the model to honor and the contract we validate on its response.
 *
 * OpenAI strict mode forbids optional keys: all fields are required, with
 * `z.nullable(...)` used for values the model is allowed to omit (empty
 * page hints, missing evidence excerpt, single-building files without a
 * building_instance_id, etc).
 */

import { z } from 'zod';
import {
  blueprintSubtypeSchema,
  projectFileClassificationTypeSchema,
  projectFileDocumentKindSchema,
} from '../models/project-file-batch.model';

/** OpenAI structured output name for file classification. */
export const PROJECT_FILE_CLASSIFIER_STRUCTURED_OUTPUT_NAME =
  'project_file_classifications' as const;

const projectFileClassifierSegmentBaseSchema = z.object({
  type: projectFileClassificationTypeSchema.describe(
    'Classification `type` — must be one of the enum values (snake_case labels such as title_block or blueprint).',
  ),
  blueprint_subtype: z
    .nullable(blueprintSubtypeSchema)
    .describe(
      'REQUIRED non-null when `type` === "blueprint"; null for every other type. `top_view` for plan-style drawings (entréplan, normalplan, översta plan, källarplan/garageplan, gårdsplan, takplan); `side_view` for sektion or fasad. Situationsplan / nybyggnadskarta is captured at file level via `document_kind = site_plan_landscape` and does NOT get a blueprint subtype — classify those segments under a different `type` when applicable, or leave this field null.',
    ),
  confidence: z
    .number()
    .describe(
      'Confidence between 0 and 1; use lower values when the choice is uncertain.',
    ),
  page_hint: z
    .nullable(z.string())
    .describe(
      'Page range or location hint (e.g. "1-3", "12"); use null if unknown.',
    ),
  pages: z
    .array(z.number().int())
    .describe(
      'Every 1-based page of the uploaded file this segment is on, in order. A page that holds several segments is listed in each of them. Empty for an image file, or when the segment cannot be placed on a page.',
    ),
  notes: z
    .nullable(z.string())
    .describe('Brief reason or caveat; use null if none.'),
  evidence_excerpt: z
    .nullable(z.string())
    .describe(
      'Quote from the file (title strip, heading, stamp) backing this segment; use null if none.',
    ),
  building_instance_id: z
    .nullable(z.string())
    .describe(
      'First id in `building_instance_ids`, or null when that array is empty. Kept so one-building segments stay a single slug.',
    ),
  building_instance_ids: z
    .array(z.string())
    .describe(
      'Every physical building whose plan, elevation, or section this segment shows. One slug when the segment shows one building. Several when one sheet shows several (a site plan, a shared plan, "Hus 1-3"). Empty when the sheets show no building volume. Repeat the same slug on every segment of the same building. Do not invent a slug from the project name, from a paper format in the stamp, or from a token that is a segment of the drawing number, including when the stamp repeats that token beside the scale or in a building-name field. Keep the id for the building the project name or the sheet title already names.',
    ),
  building_label: z
    .nullable(z.string())
    .describe(
      'Name of the physical building as printed. Null when the stamp only gives the project, a paper format, the drawing number, or a token repeated from that number.',
    ),
  accessory_volumes_on_main_block: z
    .array(
      z.object({
        printed_label: z
          .string()
          .describe(
            'Short quote of the sheet label for the accessory volume (stair/elevator, ramp, kiosk on the inner court).',
          ),
        part_of_building_instance_id: z
          .string()
          .describe(
            'The `building_instance_id` of the main block this accessory belongs to; must already be listed in `building_instance_ids`.',
          ),
      }),
    )
    .describe(
      'Small volumes that are physically part of the main scored building (courtyard stair/elevator to garage, ramp head) and must not receive their own building_instance_id. Empty when every shown volume is a freestanding building with its own area-schedule BTA or site-plan footprint.',
    ),
  title_block: z
    .nullable(
      z.object({
        drawing_id: z
          .nullable(z.string())
          .describe('Drawing number / id as printed; null if illegible.'),
        revision: z
          .nullable(z.string())
          .describe('Revision id as printed; null if absent.'),
        project_title: z
          .nullable(z.string())
          .describe(
            'Project name as printed on the stamp, including a place printed with that name. Do not shorten it to the building name. Null if absent.',
          ),
      }),
    )
    .describe(
      'Stamp fields read from the title block. Fill on `title_block` segments; null for every other type.',
    ),
});

/**
 * Cross-field invariant: `blueprint_subtype` is required exactly when
 * `type === "blueprint"`. The OpenAI JSON Schema cannot encode this rule, so
 * it lives on the Zod schema only — invalid combinations are caught when we
 * `safeParse` the model output server-side.
 */
export const projectFileClassifierSegmentSchema =
  projectFileClassifierSegmentBaseSchema.superRefine((segment, ctx) => {
    if (segment.type === 'blueprint' && segment.blueprint_subtype === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['blueprint_subtype'],
        message: '`blueprint_subtype` is required when `type` is "blueprint".',
      });
      return;
    }
    if (segment.type !== 'blueprint' && segment.blueprint_subtype !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['blueprint_subtype'],
        message:
          '`blueprint_subtype` must be null when `type` is not "blueprint".',
      });
    }
  });
export type ProjectFileClassifierSegment = z.infer<
  typeof projectFileClassifierSegmentSchema
>;

export const projectFileClassifierOutputSchema = z.object({
  segments: z
    .array(projectFileClassifierSegmentSchema)
    .min(1)
    .describe(
      'Each element classifies one logical sub-document or building-specific occurrence within the file. Repeated `type` values are allowed when the upload contains multiple buildings or multiple distinct sections of the same class (keep `building_instance_id` to preserve separation).',
    ),
  suggested_project_name: z
    .nullable(z.string())
    .describe(
      'Project name as printed on the stamp, cover, or heading, including a place printed with that name. Do not shorten it to the building name. Null if absent or uncertain.',
    ),
  suggested_project_description: z
    .nullable(z.string())
    .describe(
      'One or two sentences describing the project scope or building from the document’s own text; null if not grounded.',
    ),
  document_kind: projectFileDocumentKindSchema.describe(
    'Single whole-file document kind (not the same as per-segment `type`). Pick the best fit for the dominant content.',
  ),
  document_kind_confidence: z
    .number()
    .min(0)
    .max(1)
    .describe(
      '0–1 confidence in `document_kind`; lower when mixed or uncertain.',
    ),
});
export type ProjectFileClassifierOutput = z.infer<
  typeof projectFileClassifierOutputSchema
>;

/** Strip `$schema` meta — OpenAI structured outputs don't need it. */
const toOpenAiStrictJsonSchema = (
  schema: z.ZodType,
): Record<string, unknown> => {
  const { $schema: _ignored, ...rest } = z.toJSONSchema(schema) as Record<
    string,
    unknown
  > & { $schema?: string };
  return rest;
};

/**
 * Root strict JSON Schema for classifier structured output
 * (`project_file_classifications`). Derived from
 * {@link projectFileClassifierOutputSchema}.
 */
export const buildProjectFileClassifierStructuredOutputRootJsonSchema =
  (): Record<string, unknown> =>
    toOpenAiStrictJsonSchema(projectFileClassifierOutputSchema);
